/**
 * Date Helper Utilities - ogestyle Platform
 * 
 * Centralized date formatting and handling to ensure consistency
 * across the entire application.
 * 
 * Key principles:
 * - Dates stored as YYYY-MM-DD strings in database
 * - Display format: MM/DD/YYYY for US users
 * - All functions handle both Date objects (legacy) and strings (new)
 */

/**
 * Format a date value for display (MM/DD/YYYY)
 * Handles both YYYY-MM-DD strings and Date objects
 * 
 * @param {string|Date} dateValue - Date to format
 * @returns {string} Formatted date string (MM/DD/YYYY) or empty string if invalid
 */
export const formatDisplayDate = (dateValue) => {
  if (!dateValue) return '';
  
  // Handle YYYY-MM-DD string format (new format)
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split('-');
    return `${month}/${day}/${year}`;
  }
  
  // Handle Date objects (legacy format)
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) {
    return String(dateValue); // Return as-is if can't parse
  }
  
  // Adjust for timezone offset to get correct date
  const adjusted = new Date(date.getTime() + Math.abs(date.getTimezoneOffset() * 60000));
  const month = String(adjusted.getMonth() + 1).padStart(2, '0');
  const day = String(adjusted.getDate()).padStart(2, '0');
  const year = adjusted.getFullYear();
  
  return `${month}/${day}/${year}`;
};

/**
 * Parse any date input to YYYY-MM-DD string format
 * 
 * @param {string|Date} dateInput - Date to normalize
 * @returns {string} YYYY-MM-DD string or empty string if invalid
 */
export const parseToYYYYMMDD = (dateInput) => {
  if (!dateInput) return '';
  
  // Already in correct format
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return dateInput;
  }
  
  // Convert Date object or other string format
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) {
    return '';
  }
  
  // Use UTC to avoid timezone issues
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Get today's date as YYYY-MM-DD string
 * 
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Compare two dates (works with both strings and Date objects)
 * 
 * @param {string|Date} date1 - First date
 * @param {string|Date} date2 - Second date
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDates = (date1, date2) => {
  const d1 = parseToYYYYMMDD(date1);
  const d2 = parseToYYYYMMDD(date2);
  
  if (d1 < d2) return -1;
  if (d1 > d2) return 1;
  return 0;
};

/**
 * Check if a date is in the past (before today)
 * 
 * @param {string|Date} dateValue - Date to check
 * @returns {boolean} True if date is before today
 */
export const isPastDate = (dateValue) => {
  const date = parseToYYYYMMDD(dateValue);
  const today = getTodayString();
  return date < today;
};

/**
 * Check if a date is today
 * 
 * @param {string|Date} dateValue - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (dateValue) => {
  const date = parseToYYYYMMDD(dateValue);
  const today = getTodayString();
  return date === today;
};

/**
 * Check if a date is in the future (after today)
 * 
 * @param {string|Date} dateValue - Date to check
 * @returns {boolean} True if date is after today
 */
export const isFutureDate = (dateValue) => {
  const date = parseToYYYYMMDD(dateValue);
  const today = getTodayString();
  return date > today;
};

/**
 * Format time from 24-hour to 12-hour format with AM/PM
 * 
 * @param {string} time24 - Time in HH:MM format (e.g., "14:30")
 * @returns {string} Time in 12-hour format (e.g., "2:30 PM")
 */
export const formatTime12Hour = (time24) => {
  if (!time24 || typeof time24 !== 'string') return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time24;
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
  
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Format date range for display
 * 
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted range (e.g., "12/25/2025 - 12/26/2025")
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatDisplayDate(startDate);
  const end = formatDisplayDate(endDate);
  
  if (!start || !end) return '';
  if (start === end) return start; // Same day
  
  return `${start} - ${end}`;
};

/**
 * Get relative time description (e.g., "Today", "Yesterday", "2 days ago")
 * 
 * @param {string|Date} dateValue - Date to describe
 * @returns {string} Relative description
 */
export const getRelativeDate = (dateValue) => {
  const date = parseToYYYYMMDD(dateValue);
  const today = getTodayString();
  
  if (date === today) return 'Today';
  
  // Calculate days difference
  const dateObj = new Date(date);
  const todayObj = new Date(today);
  const diffTime = todayObj - dateObj;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) return 'Yesterday';
  if (diffDays === -1) return 'Tomorrow';
  if (diffDays > 0 && diffDays <= 7) return `${diffDays} days ago`;
  if (diffDays < 0 && diffDays >= -7) return `In ${Math.abs(diffDays)} days`;
  
  // Otherwise return formatted date
  return formatDisplayDate(dateValue);
};

/**
 * Validate date string format (YYYY-MM-DD)
 * 
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid YYYY-MM-DD format
 */
export const isValidDateString = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return false;
  
  // Check format
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return false;
  
  // Check if it's a real date
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

/**
 * Get start of week date (Sunday)
 * 
 * @returns {string} YYYY-MM-DD string for start of current week
 */
export const getStartOfWeek = () => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek);
  
  return parseToYYYYMMDD(startOfWeek);
};

/**
 * Get start of month date
 * 
 * @returns {string} YYYY-MM-DD string for first day of current month
 */
export const getStartOfMonth = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  
  return parseToYYYYMMDD(startOfMonth);
};

/**
 * Add days to a date
 * 
 * @param {string|Date} dateValue - Starting date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {string} New date in YYYY-MM-DD format
 */
export const addDays = (dateValue, days) => {
  const dateStr = parseToYYYYMMDD(dateValue);
  if (!dateStr) return '';
  
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days);
  
  return parseToYYYYMMDD(date);
};

/**
 * Format date for CSV export (consistent format)
 * 
 * @param {string|Date} dateValue - Date to format
 * @returns {string} Date formatted as MM/DD/YYYY for CSV
 */
export const formatDateForCSV = (dateValue) => {
  return formatDisplayDate(dateValue); // Same as display format
};

/**
 * Format datetime for CSV export
 * 
 * @param {string|Date} dateValue - Date to format
 * @param {string} timeValue - Time in HH:MM format
 * @returns {string} Combined datetime string
 */
export const formatDateTimeForCSV = (dateValue, timeValue) => {
  const date = formatDisplayDate(dateValue);
  const time = formatTime12Hour(timeValue);
  return `${date} ${time}`;
};
